/**
 * RPC 客户端运行时（浏览器/同源）。
 *
 * 应用 ssr:false，所有数据拉取在客户端——无需 SSR 进程内 client。
 * client 的生命周期跟随 app（scope = runtime.scope）。
 */
import { Data, Effect, Layer, ManagedRuntime, Scope } from "effect";
import * as FetchHttpClient from "effect/unstable/http/FetchHttpClient";
import * as RpcClient from "effect/unstable/rpc/RpcClient";
import * as RpcSerialization from "effect/unstable/rpc/RpcSerialization";

const ProtocolLive = Layer.provide(
  RpcClient.layerProtocolHttp({ url: "/api/rpc" }),
  Layer.mergeAll(RpcSerialization.layerJson, FetchHttpClient.layer),
);

const runtime = ManagedRuntime.make(ProtocolLive);

// 以组对象自身为键（WeakMap）：minify 改写 constructor.name 也不会串组
const clientCache = new WeakMap<
  object,
  Promise<Record<string, (payload: unknown) => Effect.Effect<unknown, unknown>>>
>();

/** 取某契约组的类型化 client（按组缓存，常驻） */
export function rpcClient<Group extends object>(group: Group) {
  let cached = clientCache.get(group);
  if (!cached) {
    cached = runtime.runPromise(
      Effect.provideService(RpcClient.make(group as never), Scope.Scope, runtime.scope),
    ) as unknown as Promise<Record<string, (payload: unknown) => Effect.Effect<unknown, unknown>>>;
    clientCache.set(group, cached);
  }
  return cached;
}

/** 调用一个 RPC（Promise 世界入口，供 queryAtom/mutation runner 使用） */
export function callRpc<A>(group: object, method: string, payload: unknown): Promise<A> {
  return rpcClient(group).then((client) =>
    runtime.runPromise(
      Effect.provideService(
        client[method]!(payload) as Effect.Effect<A, unknown, Scope.Scope>,
        Scope.Scope,
        runtime.scope,
      ),
    ),
  );
}

// ── 类型化调用门面 ──────────────────────────────────────────────────────────

import type * as Rpc from "effect/unstable/rpc/Rpc";
import type { RpcGroup as RpcGroupType } from "effect/unstable/rpc/RpcGroup";

/**
 * 类型化调用器：方法级 payload/success 类型从契约组推导。
 * 取代字符串 tag + 手动泛型的 callRpc（收紧 WireJson 后编译期即可拦截漂移）。
 */
export type RpcCaller<Rpcs extends Rpc.Any> = {
  readonly [K in Rpcs["_tag"]]: (
    payload: Rpc.PayloadConstructor<Extract<Rpcs, { _tag: K }>>,
  ) => Promise<Rpc.Success<Extract<Rpcs, { _tag: K }>>>;
};

const callerCache = new WeakMap<object, unknown>();

/** 取某契约组的类型化调用器（按组缓存；浏览器侧 HTTP） */
export function rpcCaller<Rpcs extends Rpc.Any>(group: RpcGroupType<Rpcs>): RpcCaller<Rpcs> {
  let cached = callerCache.get(group);
  if (!cached) {
    cached = new Proxy({} as Record<string, unknown>, {
      get: (_target, method: string) => (payload: unknown) => callRpc(group, method, payload),
    });
    callerCache.set(group, cached);
  }
  return cached as RpcCaller<Rpcs>;
}

// ── Effect-native 调用面（queryAtomEffect / useMutationCommand 的数据源）──────

/**
 * 类型化 Effect 调用器：方法级 payload/success/error 类型从契约组推导，
 * 返回 RPC 原生 `Effect<Success, Error, Scope>`（本模块在执行终端注入 scope）。
 *
 * 这是 Effect 硬切的核心出口：错误通道不再被 Promise 化擦除，
 * queryAtomEffect 可直接消费（保留 RpcAppError 等 typed failures）。
 */
export class RpcClientBootstrapError extends Data.TaggedError("RpcClientBootstrapError")<{
  readonly cause: unknown;
}> {}

export type RpcEffectCaller<Rpcs extends Rpc.Any> = {
  readonly [K in Rpcs["_tag"]]: (
    payload: Rpc.PayloadConstructor<Extract<Rpcs, { _tag: K }>>,
  ) => Effect.Effect<
    Rpc.Success<Extract<Rpcs, { _tag: K }>>,
    Rpc.Error<Extract<Rpcs, { _tag: K }>> | RpcClientBootstrapError,
    never
  >;
};

const effectCallerCache = new WeakMap<object, unknown>();

/** 取某契约组的 Effect 调用器（按组缓存；Scope 由内部 runtime 终端提供） */
export function rpcEffectCaller<Rpcs extends Rpc.Any>(
  group: RpcGroupType<Rpcs>,
): RpcEffectCaller<Rpcs> {
  let cached = effectCallerCache.get(group);
  if (!cached) {
    cached = new Proxy({} as Record<string, unknown>, {
      get: (_target, method: string) => (payload: unknown) =>
        invokeRpcEffect(group, method, payload),
    });
    effectCallerCache.set(group, cached);
  }
  return cached as RpcEffectCaller<Rpcs>;
}

function rpcClientEffect(
  group: object,
): Effect.Effect<
  Record<string, (payload: unknown) => Effect.Effect<unknown, unknown, Scope.Scope>>,
  RpcClientBootstrapError
> {
  return Effect.tryPromise({
    try: () => rpcClient(group),
    catch: (cause) => new RpcClientBootstrapError({ cause }),
  });
}

/** Runtime-faithful invocation: contract failure remains unknown until the typed facade narrows it. */
function invokeRpcEffect(
  group: object,
  method: string,
  payload: unknown,
): Effect.Effect<unknown, unknown> {
  return Effect.flatMap(rpcClientEffect(group), (client) =>
    client[method]!(payload).pipe(Effect.provideService(Scope.Scope, runtime.scope)),
  );
}
